import type {
	ChatMessageContent,
	MembershipRole,
	UserSummaryDTO,
} from "@zplanem/responses"
import type { Translations } from "@zplanem/translations"
import { getDisplayName } from "./user-display"

const MEMBERSHIP_ROLES = ["member", "contractor", "architect"] as const
function isMembershipRole(s: string | undefined): s is MembershipRole {
	return s !== undefined && (MEMBERSHIP_ROLES as readonly string[]).includes(s)
}

interface RenderChatMessageContext {
	t: Translations
	/**
	 * Resolves a `userId` to a `UserSummaryDTO`, typically from the
	 * memberships cache. Used by templated messages that store user ids
	 * rather than pre-rendered names — keeps the row correct as people
	 * complete onboarding or update their name. Returns `null` for ids
	 * that are no longer in scope (ex-members, etc.).
	 */
	resolveUser?: (userId: string) => UserSummaryDTO | null
}

/**
 * Renders a chat message body for the current viewer.
 *
 * - `{ message }` content is returned verbatim — used for everything the
 *   user typed or the AI free-form-replied with.
 * - `{ templateKey, templateParams }` content is rendered against
 *   `Translations` so every viewer sees the localized version. Each known
 *   template has hand-rolled interpolation here so role-typed params
 *   (currently just `role`) get their nested locale lookup.
 *
 * Unknown templates degrade gracefully to an empty string rather than
 * leaking the raw key. `content` is always the canonical jsonb shape per
 * the wire contract — any environment seeing a raw string here is missing
 * drizzle migration 0013.
 */
export function renderChatMessage(
	content: ChatMessageContent,
	ctx: RenderChatMessageContext,
): string {
	if ("message" in content) return content.message
	// Approval cards are rendered by AgentActionCard, not as text — callers
	// branch on `"agentAction" in content` before reaching here; degrade to
	// empty for any that don't.
	if ("agentAction" in content) return ""
	const { t, resolveUser } = ctx

	const { templateKey, templateParams } = content
	switch (templateKey) {
		case "mareczek.greetings.propertyCreated":
			return t.mareczek.greetings.propertyCreated
				.replace("{ownerName}", templateParams.ownerName ?? "")
				.replace("{propertyName}", templateParams.propertyName ?? "")
		case "mareczek.greetings.propertyJoined": {
			const userId = templateParams.userId
			const user = userId && resolveUser ? resolveUser(userId) : null
			const memberName = user ? getDisplayName(user) : "?"
			return t.mareczek.greetings.propertyJoined
				.replace("{memberName}", memberName)
				.replace("{propertyName}", templateParams.propertyName ?? "")
		}
		case "mareczek.systemMessages.memberJoined": {
			const userId = templateParams.userId
			const user = userId && resolveUser ? resolveUser(userId) : null
			const fullName = user ? getDisplayName(user) : "?"
			const role = templateParams.role
			const roleLabel = isMembershipRole(role)
				? t.properties.share.roles[role]
				: (role ?? "")
			return t.mareczek.systemMessages.memberJoined
				.replace("{fullName}", fullName)
				.replace("{role}", roleLabel)
		}
		case "mareczek.agentActions.executed": {
			const summary = templateParams?.summary
			return summary
				? t.mareczek.agentActions.executedSummary.replace("{summary}", summary)
				: t.mareczek.agentActions.executed
		}
		case "mareczek.agentActions.failed":
			return t.mareczek.agentActions.failed
		case "mareczek.agentActions.declined":
			return t.mareczek.agentActions.declined
		case "mareczek.agentActions.chainFailed":
			return t.mareczek.agentActions.chainFailed
		default:
			return ""
	}
}
